import React, { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { WhatsAppProvider } from './context/WhatsAppContext';
import { detectKeyboardUser } from './utils/accessibility';
import ErrorBoundary from './components/common/ErrorBoundary';

// Import responsive styles
import './styles/responsive.css';

// Layouts
import Layout from './components/layout/Layout';

// Pages
import LoginPage from './pages/LoginPage';
import RegistroPage from './pages/RegistroPage';
import JoinPage from './pages/JoinPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import DashboardPage from './pages/DashboardPage';
import PacientesPage from './pages/PacientesPage';
import AdminCitasPage from './pages/AdminCitasPage';
import FichaClinicaPage from './pages/FichaClinicaPage';
import InventarioPage from './pages/InventarioPage';
import RespaldoPage from './pages/RespaldoPage';
import TeamPage from './pages/TeamPage';
import GestionClinicasPage from './pages/SuperAdmin/GestionClinicasPage';
import PlanExpiredPage from './pages/PlanExpiredPage';
import RentabilidadPage from './pages/RentabilidadPage';
import SubscriptionPage from './pages/SubscriptionPage';
import ConfirmSubscriptionPage from './pages/ConfirmSubscriptionPage';
import ConfiguracionClinicaPage from './pages/ConfiguracionClinicaPage';
import TratamientosPage from './pages/TratamientosPage';

// Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import PlanGuard from './components/auth/PlanGuard';
import PublicRoutes from './routes/PublicRoutes';

const App = () => {
  // Detectar usuarios de teclado para mejorar accesibilidad
  useEffect(() => {
    const cleanup = detectKeyboardUser();
    return cleanup;
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <WhatsAppProvider>
          <Router>
            {/* Skip to main content link for accessibility */}
            <a href="#main-content" className="skip-link">
              Saltar al contenido principal
            </a>
            <Toaster position="top-right" toastOptions={{
              style: { fontSize: '0.9rem' },
              success: { iconTheme: { primary: '#16a34a', secondary: 'white' } },
            }} />
            <Routes>
            {/* Ruta raíz redirige a app/login */}
            <Route path="/" element={<Navigate to="/app/login" replace />} />

            {/* Redirigir /login a /app/login para evitar problemas de navegación */}
            <Route path="/login" element={<Navigate to="/app/login" replace />} />
            {/* Redirigir /registro a /app/registro para mantener compatibilidad */}
            <Route path="/registro" element={<Navigate to="/app/registro" replace />} />

            {/* Compatibilidad con rutas antiguas - redirigir /admin/* a /app/* */}
            <Route path="/admin/login" element={<Navigate to="/app/login" replace />} />
            <Route path="/admin/dashboard" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/admin/pacientes" element={<Navigate to="/app/pacientes" replace />} />
            <Route path="/admin/citas" element={<Navigate to="/app/citas" replace />} />
            <Route path="/admin/fichas-clinicas" element={<Navigate to="/app/fichas-clinicas" replace />} />
            <Route path="/admin/inventario" element={<Navigate to="/app/inventario" replace />} />
            <Route path="/admin/respaldo" element={<Navigate to="/app/respaldo" replace />} />

            {/* Rutas públicas */}
            <Route path="/auth/join/:token" element={<JoinPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/password-reset/:uid/:token" element={<ResetPasswordPage />} />
            <Route path="/auth/activate/:uid/:token" element={<VerifyEmailPage />} />
            <Route path="/public/*" element={<PublicRoutes />} />

            {/* Rutas protegidas de la aplicación */}
            <Route path="/app/login" element={<LoginPage />} />
            <Route path="/app/registro" element={<RegistroPage />} />

            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Navigate to="/app/dashboard" replace />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/app/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DashboardPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/app/pacientes"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PacientesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/app/citas"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminCitasPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/app/fichas-clinicas"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FichaClinicaPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/app/inventario"
              element={
                <ProtectedRoute>
                  <PlanGuard requiresInventory>
                    <Layout>
                      <InventarioPage />
                    </Layout>
                  </PlanGuard>
                </ProtectedRoute>
              }
            />

            <Route
              path="/app/equipo"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TeamPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/app/respaldo"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RespaldoPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/app/rentabilidad"
              element={
                <ProtectedRoute>
                  <PlanGuard requiresFinance>
                    <Layout>
                      <RentabilidadPage />
                    </Layout>
                  </PlanGuard>
                </ProtectedRoute>
              }
            />

            <Route
              path="/app/admin/clinicas"
              element={
                <ProtectedRoute>
                  <Layout>
                    <GestionClinicasPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/app/suscripcion/confirmar"
              element={
                <ProtectedRoute>
                  <ConfirmSubscriptionPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/app/suscripcion"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SubscriptionPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route path="/app/plan-expired" element={<PlanExpiredPage />} />

            <Route
              path="/app/configuracion"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ConfiguracionClinicaPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/app/configuracion/tratamientos"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TratamientosPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            </Routes>
          </Router>
        </WhatsAppProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;